import { useMutation, useQuery } from "@apollo/client";
import { Button, DatePicker, Form, Input, message, TimePicker } from "antd";
import dayjs from "dayjs";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { UserContext } from "../../../amplify/authenticator-provider";
import { CREATE_SCHEDULED_MESSAGE, UPDATE_SCHEDULED_MESSAGE } from "../../../graphql/mutation";
import { GET_COMPANY_WORK_HOURS } from "../../../graphql/query";
import { timezones } from "../../../library/constants";
import { addScheduledMessage, updateScheduledMessageRedux } from "../../../redux/slices/leadSlice";
import { convertUtcToLocal } from "../../../library/utils";

const utc = require("dayjs/plugin/utc");

dayjs.extend(utc);

const ScheduleMessagesPopup = ({
  scheduledMessages,
  getMessages,
  messageData: editMessageData,
  selectedLead,
  mode = "EDIT",
  setmessageText = () => {},
  reset = () => {},
  close = () => {},
}) => {
  const [form] = Form.useForm();
  const { user } = useContext(UserContext);
  const dispatch = useDispatch();

  // Determine the correct timezone based on context
  const timeZone = useMemo(() => {
    const currentLocationId = sessionStorage.getItem("currentLocationId");

    // Multi-location context: use user's location timezone
    if (currentLocationId?.includes(",")) {
      return user?.locations?.find(item => item.location.id === user?.locationId)?.location
        ?.timezone;
    }

    // Single location context: use session location timezone
    const sessionLocation = user?.locations?.find(item => item.location.id === currentLocationId);
    return sessionLocation?.location?.timezone;
  }, [user?.locations, user?.locationId]);

  const initialState = {
    message: editMessageData?.message,
    date:
      mode === "EDIT" ? dayjs(editMessageData?.taskSendDatetime).tz(timeZone).toISOString() : null,
    timezone: timeZone,
  };
  const [workingHours, setworkingHours] = useState([]);
  const [createScheduledMessage, { loading: createLoading }] =
    useMutation(CREATE_SCHEDULED_MESSAGE);
  const [updateScheduledMessage, { loading: updateLoading }] =
    useMutation(UPDATE_SCHEDULED_MESSAGE);
  useQuery(GET_COMPANY_WORK_HOURS, {
    variables: {
      companyId: user.company.id,
      locationId: (sessionStorage.getItem("currentLocationId") || "").includes(",")
        ? selectedLead?.location?.id
        : sessionStorage.getItem("currentLocationId"),
    },
    onCompleted: res => {
      setworkingHours(res.getCompanyWorkingHours);
    },
  });
  const [messageData, setmessageData] = useState(initialState);

  // useEffect(() => {
  //   const obj = timezones.find(item => item.value === dayjs.tz.guess());
  //   console.log(obj);
  //   if (obj) setmessageData({ ...messageData, timezone: obj.label });
  //   else {
  //     setmessageData({ ...messageData, timezone: dayjs.tz.guess() });
  //   }
  // }, []);
  const getTimezoneLabel = () => {
    const obj = timezones.find(item => item.value === messageData.timezone);
    if (obj) {
      return obj.label;
    }
    return messageData.timezone;
  };
  // const timeZoneLabel = useMemo(() => getTimezoneLabel(), messageData.timezone);
  const timeZoneLabel = messageData.timezone;

  useEffect(() => {
    if (mode === "EDIT" && editMessageData?.taskSendDatetime) {
      const dateValue = dayjs(editMessageData?.taskSendDatetime, "YYYY-MM-DDTHH:mm:ss")
        .utc(true)
        .toISOString();
      setmessageData({
        message: editMessageData?.message,
        date: dateValue,
        time: dateValue,
        timezone: timeZone,
      });
    } else if (mode !== "EDIT") {
      setmessageData({
        message: editMessageData?.message,
        date: null,
        timezone: timeZone,
      });
    }
  }, []);
  const handleSave = async () => {
    try {
      if (messageData.message === "") {
        return message.error("Please enter the message you would like to send in the future");
      }
      if (mode === "EDIT") {
        const variables = {
          id: editMessageData?.id,
          scheduledDate: dayjs(messageData?.date).format("YYYY-MM-DDTHH:mm:ss"),
          timezone: timeZone,
          message: messageData?.message,
        };
        const resp = await updateScheduledMessage({
          variables,
        });
        if (resp?.data?.updateScheduledMessage?.statusCode === 200) {
          message.success("Scheduled message updated successfully");
          const index = scheduledMessages.findIndex(item => item?.id === editMessageData?.id);
          if (index > -1) {
            dispatch(
              updateScheduledMessageRedux({
                index,
                message: resp?.data?.updateScheduledMessage?.scheduledMessage,
              })
            );
          }
          setmessageText("");
          reset();
          close();
          // Refetch messages from page 1 to show the updated scheduled message
          getMessages({
            variables: {
              leadId: selectedLead?.id,
              page: 1,
            },
          });
        } else {
          console.error(resp?.data?.updateScheduledMessage?.message);
          message.error("Message failed to schedule. Please try again or contact support.");
        }
      } else {
        const variables = {
          leadId: selectedLead?.id,
          userId: user?.id,
          scheduledDate: dayjs(messageData?.date).format("YYYY-MM-DDTHH:mm:ss"),
          timezone: timeZone,
          message: messageData?.message,
        };
        const resp = await createScheduledMessage({
          variables,
        });

        if (resp?.data?.createScheduledMessage?.statusCode === 200) {
          setmessageText("");
          reset();
          message.success(resp?.data?.createScheduledMessage?.message);
          dispatch(addScheduledMessage(resp.data.createScheduledMessage.scheduledMessage));
          close();
          // Refetch messages from page 1 to show the new scheduled message
          getMessages({
            variables: {
              leadId: selectedLead?.id,
              page: 1,
            },
          });
        } else {
          message.error("Message failed to schedule. Please try again or contact support.");
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (mode === "EDIT") {
      form.setFieldsValue({
        message: messageData?.message || "",
        date: dayjs(messageData.date),
        time: dayjs(messageData.date),
      });
    } else {
      form.setFieldsValue({
        message: messageData?.message || "",
      });
    }
  }, [mode, messageData]);
  const isWorkingDay = date => {
    const today = dayjs();
    const selectedDate = dayjs(date);

    // Check if the selected date is in the past
    if (selectedDate.isBefore(today, "day")) {
      return false;
    }

    const dayOfWeek = selectedDate.format("dddd").toUpperCase();
    const workingDay = workingHours.find(hours => hours.weekDay === dayOfWeek);

    return workingDay ? workingDay.isWorkingDay : false;
  };

  const disabledDate = current => !isWorkingDay(current);
  const disabledTime = current => {
    const dayOfWeek = dayjs(current).format("dddd").toUpperCase();
    const workingDay = workingHours.find(hours => hours.weekDay === dayOfWeek);

    if (workingDay && workingDay.isWorkingDay) {
      const startTime = dayjs(workingDay.startTime, "HH:mm:ss");
      const endTime = dayjs(workingDay.endTime, "HH:mm:ss");
      return {
        disabledHours: () =>
          [...Array(24).keys()].filter(h => h < startTime.hour() || h >= endTime.hour()),
        disabledMinutes: () => [],
      };
    }

    return {};
  };
  return (
    <div>
      <Form form={form} onFinish={() => handleSave()} layout="vertical">
        {mode !== "ADD" && (
          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: "Please enter the message you want to schedule" }]}
          >
            <Input.TextArea
              className="rounded-[2px]"
              name="message"
              rows={4}
              placeholder="Enter the message you want to schedule"
              value={messageData?.message}
              defaultValue={messageData?.message}
              onChange={e => setmessageData(prev => ({ ...prev, message: e.target.value }))}
            />
          </Form.Item>
        )}
        <p className="mt-2 mb-1">Date and time you want the message to be delivered</p>
        <div className="flex-row-centered gap-1">
          <Form.Item
            className="w-[60%]"
            label="Due Date"
            rules={[{ required: true, message: "Please select date" }]}
            name="date"
          >
            <DatePicker
              className="w-full rounded-[2px]"
              name="date"
              onChange={value => {
                if (value)
                  setmessageData(prev => ({ ...prev, date: value.toISOString(), rawDate: value }));
              }}
              format="MM/DD/YYYY"
              disabledDate={disabledDate}
            />
          </Form.Item>
          <Form.Item
            label="Due Time"
            className="w-[40%]"
            rules={[{ required: true, message: "Please select time" }]}
            name="time"
          >
            <TimePicker
              name="time"
              className="w-full rounded-[2px]"
              format="hh:mm A"
              disabled={!messageData.date}
              disabledTime={disabledTime}
              // value={dayjs(messageData.date)}
              onChange={value => {
                if (value && messageData.date) {
                  const updatedDate = dayjs(messageData.date)
                    .hour(dayjs(value).hour())
                    .minute(dayjs(value).minute());
                  setmessageData(prev => ({
                    ...prev,
                    date: updatedDate.toISOString(),
                  }));
                }
              }}
            />
          </Form.Item>
        </div>
        <p className="mt-1">
          Current timezone: <b>{timeZoneLabel}</b>
        </p>
        <span className="text-xs text-grey-700">
          The message will be delivered to the lead on{" "}
          <span className="font-[500]">
            {convertUtcToLocal(messageData.date, "DD/MM/YYYY hh:mm A")}
          </span>
        </span>
      </Form>
      <div className="flex-row-centered mt-1 justify-start gap-2">
        <Button
          type="primary"
          loading={updateLoading || createLoading}
          onClick={() => form.submit()}
          className="bg-grey-700"
        >
          {mode === "EDIT" ? "Re-schedule Message" : "Schedule Message"}
        </Button>{" "}
        <Button
          onClick={() => {
            close();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ScheduleMessagesPopup;
